<script setup>
import { computed, watch, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useKanban } from 'dashboard/composables/useKanban';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import kanbanCardsAPI from 'dashboard/api/kanbanCards';
import KanbanColumn from './components/KanbanColumn.vue';
import KanbanHeader from './components/KanbanHeader.vue';
import AddColumnModal from './components/AddColumnModal.vue';
import ConversationFilter from 'dashboard/components-next/filter/ConversationFilter.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const {
  loading,
  error,
  pipeline,
  columns,
  loadPipelineBoard,
  loadMoreConversations,
  moveConversation,
  removeConversationFromPipeline,
  fetchColumns,
  createColumn,
} = useKanban();

// Estado de paginação por coluna
const columnPages = ref({});

// Pipeline management
const pipelines = ref([]);
const showPipelineDropdown = ref(false);

// useKanbanFilters removido

// Função de filtro removida

const pipelineId = computed(() => route.params.pipelineId);

// Add column modal state
const showAddColumnModal = ref(false);
const addColumnLoading = ref(false);

// Filtro de status do card (filtro da direita)
const currentCardStatusFilter = ref('open'); // Inicia em "em aberto"

// Modal de filtros de conversa
const showConversationFilterModal = ref(false);
const appliedFilters = ref([
  {
    attributeKey: 'status',
    filterOperator: 'equal_to',
    values: ['all'],
    queryOperator: 'and',
  },
]);

// Auto-scroll functionality
const isDragging = ref(false);
const scrollContainer = ref(null);
const scrollSpeed = ref(0);
const scrollInterval = ref(null);

// Delete confirmation modal state
const showDeleteModal = ref(false);
const conversationToDelete = ref(null);

// Aplicar filtros combinados (filtros de conversa + status do card)
const filteredColumns = computed(() => {
  return columns.value.map(column => {
    if (!column.conversations) return column;

    // Filtrar conversas baseado nos filtros aplicados e status do card
    const filteredConversations = column.conversations.filter(conversation => {
      const kanbanCard = conversation.kanban_cards?.find(
        card => String(card.kanban_pipeline_id) === String(pipelineId.value)
      );
      const cardStatus = kanbanCard?.status || 'open';

      // Aplicar filtros de conversa (se houver)
      let conversationFilterPass = true;
      if (appliedFilters.value.length > 0) {
        // Verificar se há filtros com valores válidos
        const validFilters = appliedFilters.value.filter(filter => {
          const values = Array.isArray(filter.values) ? filter.values : [];
          return values.length > 0;
        });

        if (validFilters.length > 0) {
          conversationFilterPass = validFilters.every(filter => {
            const attributeKey = filter.attribute_key || filter.attributeKey;
            const filterOperator =
              filter.filter_operator || filter.filterOperator;
            const values = filter.values || [];

            let conversationValue = conversation[attributeKey];

            // Tratar casos especiais
            if (attributeKey === 'assignee_id') {
              conversationValue = conversation.assignee?.id;
            } else if (attributeKey === 'inbox_id') {
              conversationValue = conversation.inbox?.id;
            } else if (attributeKey === 'team_id') {
              conversationValue = conversation.team_id;
            } else if (attributeKey === 'labels') {
              // Labels é um array, precisa verificar se alguma label bate
              const conversationLabels =
                conversation.labels?.map(l => l.id || l) || [];
              const filterLabelIds = values.map(v =>
                typeof v === 'object' && v.id ? v.id : v
              );

              // Para labels, verificar se há intersecção
              if (filterOperator === 'equal_to') {
                return filterLabelIds.some(labelId =>
                  conversationLabels.includes(labelId)
                );
              }
              if (filterOperator === 'not_equal_to') {
                return !filterLabelIds.some(labelId =>
                  conversationLabels.includes(labelId)
                );
              }
            } else if (attributeKey === 'browser_language') {
              conversationValue =
                conversation.additional_attributes?.browser_language;
            } else if (attributeKey === 'country_code') {
              conversationValue =
                conversation.additional_attributes?.country_code;
            } else if (attributeKey === 'referer') {
              conversationValue = conversation.additional_attributes?.referer;
            }

            // Extrair IDs dos valores (caso sejam objetos)
            const filterValues = Array.isArray(values)
              ? values.map(v => (typeof v === 'object' && v.id ? v.id : v))
              : [];

            // Tratamento especial para status "all" - aceitar qualquer conversa
            if (attributeKey === 'status' && filterValues.includes('all')) {
              return true;
            }

            let result = true;
            switch (filterOperator) {
              case 'equal_to':
                result = filterValues.includes(conversationValue);
                break;
              case 'not_equal_to':
                result = !filterValues.includes(conversationValue);
                break;
              case 'is_present':
                result =
                  conversationValue !== null &&
                  conversationValue !== undefined &&
                  conversationValue !== '';
                break;
              case 'is_not_present':
                result =
                  conversationValue === null ||
                  conversationValue === undefined ||
                  conversationValue === '';
                break;
              default:
                result = true;
            }

            return result;
          });
        }
      }

      // Aplicar filtro de status do card
      let cardFilterPass = false;
      switch (currentCardStatusFilter.value) {
        case 'all':
          cardFilterPass = true;
          break;
        case 'open':
          cardFilterPass = cardStatus === 'open';
          break;
        case 'won':
          cardFilterPass = cardStatus === 'won';
          break;
        case 'lost':
          cardFilterPass = cardStatus === 'lost';
          break;
        case 'no_response':
          cardFilterPass = cardStatus === 'no_response';
          break;
        default:
          cardFilterPass = true;
      }

      // Combinar os dois filtros (AND logic)
      const shouldShow = conversationFilterPass && cardFilterPass;

      return shouldShow;
    });

    return {
      ...column,
      conversations: filteredConversations,
    };
  });
});

const columnStats = computed(() =>
  filteredColumns.value.map(column => ({
    id: column.id,
    name: column.name,
    count: column.conversations?.length || 0,
  }))
);

// hasConversationFilters removido

const totalConversations = computed(() =>
  columnStats.value.reduce((total, column) => total + column.count, 0)
);

const hasAppliedFilters = computed(() => {
  // Verificar se há filtros aplicados diferentes do padrão "all"
  if (appliedFilters.value.length === 0) return false;

  // Se há apenas 1 filtro e é o padrão "all", não considerar como filtro aplicado
  if (appliedFilters.value.length === 1) {
    const filter = appliedFilters.value[0];
    const values = Array.isArray(filter.values) ? filter.values : [];
    return !(
      filter.attributeKey === 'status' &&
      filter.filterOperator === 'equal_to' &&
      values.includes('all')
    );
  }

  // Se há múltiplos filtros, sempre considerar como filtro aplicado
  return true;
});

const moveConversationHandler = async (
  conversationId,
  fromColumnId,
  toColumnId
) => {
  if (fromColumnId === toColumnId) return;

  try {
    // Encontrar a conversa que está sendo movida
    const fromColumn = columns.value.find(col => col.id === fromColumnId);
    const toColumn = columns.value.find(col => col.id === toColumnId);

    if (!fromColumn || !toColumn) return;

    const conversation = fromColumn.conversations?.find(
      conv => conv.id === conversationId
    );
    if (!conversation) return;

    // Criar uma cópia da conversa para evitar problemas de referência
    const conversationCopy = { ...conversation };

    // Atualizar localmente primeiro para UX responsiva
    fromColumn.conversations = fromColumn.conversations.filter(
      conv => conv.id !== conversationId
    );
    if (!toColumn.conversations) toColumn.conversations = [];
    toColumn.conversations.push(conversationCopy);

    // Fazer a requisição para o backend (sem recarregar)
    await moveConversation(pipelineId.value, toColumnId, {
      conversation_id: conversationId,
    });

    // Disparar evento para sincronizar com outros componentes
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('funnelUpdated', {
          detail: {
            conversationId,
            action: 'move',
            fromColumnId,
            toColumnId,
            pipelineId: pipelineId.value,
          },
        })
      );
    }
  } catch (error) {
    console.error('Erro ao mover conversa:', error);
    useAlert('Erro ao mover conversa');
    // Em caso de erro, reverter a mudança local
    const toColumn = columns.value.find(col => col.id === toColumnId);
    const fromColumn = columns.value.find(col => col.id === fromColumnId);
    const conversation = toColumn.conversations?.find(
      conv => conv.id === conversationId
    );

    if (conversation && toColumn && fromColumn) {
      toColumn.conversations = toColumn.conversations.filter(
        conv => conv.id !== conversationId
      );
      fromColumn.conversations.push(conversation);
    }
  }
};

// Add column handlers
const handleAddColumn = () => {
  showAddColumnModal.value = true;
};

const handleCloseAddColumnModal = () => {
  showAddColumnModal.value = false;
};

// Função para lidar com mudanças no filtro de status do card
const handleCardStatusFilterChange = status => {
  currentCardStatusFilter.value = status;
};

// Funções para lidar com o modal de filtros de conversa
const openConversationFilterModal = async () => {
  // Carregar dados primeiro se necessário
  if (!filterDataLoaded.value) {
    try {
      await loadFilterData();
    } catch (error) {
      console.error('Erro ao carregar dados do filtro:', error);
      // Mesmo com erro, continuar
    }
  }

  // Aguardar um pouco para garantir que os dados estão prontos
  await new Promise(resolve => setTimeout(resolve, 100));

  // Se não há filtros aplicados, inicializar com filtro "All" como padrão
  if (appliedFilters.value.length === 0) {
    appliedFilters.value = [
      {
        attributeKey: 'status',
        filterOperator: 'equal_to',
        values: ['all'],
        queryOperator: 'and',
      },
    ];
  }

  // Abrir o modal
  showConversationFilterModal.value = true;
};

const closeConversationFilterModal = () => {
  showConversationFilterModal.value = false;
};

const clearAllFilters = () => {
  // Resetar para o filtro padrão "all"
  appliedFilters.value = [
    {
      attributeKey: 'status',
      filterOperator: 'equal_to',
      values: ['all'],
      queryOperator: 'and',
    },
  ];
};

// Pipeline management functions
const handleSelectPipeline = selectedPipeline => {
  router.push({
    name: 'kanban_pipeline_board',
    params: { pipelineId: selectedPipeline.id },
  });
  showPipelineDropdown.value = false;
};

const handleTogglePipelineDropdown = () => {
  showPipelineDropdown.value = !showPipelineDropdown.value;
};

const handleCreatePipeline = () => {
  // Emitir evento para o KanbanRouter criar novo pipeline
  window.dispatchEvent(new CustomEvent('kanban-create-pipeline'));
  showPipelineDropdown.value = false;
};

const handleManagePipelines = () => {
  router.push({ name: 'kanban_pipelines_index' });
};

const handleApplyFilters = filters => {
  // Converter values para array se necessário
  if (filters) {
    filters.forEach(filter => {
      // Se values não for array, tentar converter
      if (filter.values && !Array.isArray(filter.values)) {
        // Se for um objeto com dados, converter para array com esse objeto
        if (typeof filter.values === 'object') {
          if (Object.keys(filter.values).length === 0) {
            // Objeto vazio -> array vazio
            filter.values = [];
          } else {
            // Objeto com dados -> array com esse objeto
            filter.values = [filter.values];
          }
        }
      }
    });
  }

  // Filtrar apenas filtros válidos (com valores)
  const validFilters = (filters || []).filter(filter => {
    const values = Array.isArray(filter.values) ? filter.values : [];
    return values.length > 0;
  });

  // Se não há filtros válidos, usar filtro padrão "all"
  if (validFilters.length === 0) {
    appliedFilters.value = [
      {
        attributeKey: 'status',
        filterOperator: 'equal_to',
        values: ['all'],
        queryOperator: 'and',
      },
    ];
  } else {
    appliedFilters.value = validFilters;
  }

  showConversationFilterModal.value = false;
};

// clearFilters removido para evitar problemas

const handleSaveColumn = async columnData => {
  try {
    addColumnLoading.value = true;
    await createColumn(pipelineId.value, {
      name: columnData.name,
      description: columnData.description,
      color: columnData.color,
      position: columns.value.length,
    });
    await fetchColumns(pipelineId.value);
    showAddColumnModal.value = false;
    useAlert(t('KANBAN.COLUMN_CREATED_SUCCESSFULLY'));
  } catch (error) {
    useAlert(t('KANBAN.COLUMN_CREATE_ERROR'));
  } finally {
    addColumnLoading.value = false;
  }
};

// Conversation management handlers
const handleDeleteConversation = conversationId => {
  conversationToDelete.value = conversationId;
  showDeleteModal.value = true;
};

const confirmDeleteConversation = async () => {
  let conversationBackup = null;
  let targetColumn = null;

  try {
    // Find the conversation and column
    targetColumn = columns.value.find(col =>
      col.conversations?.some(conv => conv.id === conversationToDelete.value)
    );

    if (targetColumn) {
      // Backup da conversa antes de remover
      conversationBackup = targetColumn.conversations.find(
        conv => conv.id === conversationToDelete.value
      );

      // Remover localmente primeiro
      targetColumn.conversations = targetColumn.conversations.filter(
        conv => conv.id !== conversationToDelete.value
      );

      // Chamar API para remover do backend
      await removeConversationFromPipeline(
        pipelineId.value,
        targetColumn.id,
        conversationToDelete.value
      );

      useAlert('Conversa removida do funil com sucesso');

      // TODO: Emitir evento via socket para outros usuários
    }
  } catch (error) {
    console.error('Erro ao remover conversa:', error);
    useAlert('Erro ao remover conversa do funil');

    // Em caso de erro, reverter a mudança local
    if (targetColumn && conversationBackup) {
      targetColumn.conversations = targetColumn.conversations || [];
      targetColumn.conversations.push(conversationBackup);
    }
  } finally {
    showDeleteModal.value = false;
    conversationToDelete.value = null;
  }
};

const cancelDeleteConversation = () => {
  showDeleteModal.value = false;
  conversationToDelete.value = null;
};

const handleUpdateConversation = updatedConversation => {
  // Find and update the conversation in the columns
  columns.value.forEach(column => {
    if (column.conversations) {
      const index = column.conversations.findIndex(
        conv => conv.id === updatedConversation.id
      );
      if (index !== -1) {
        // Deep merge to ensure kanban_cards are properly updated
        column.conversations[index] = {
          ...column.conversations[index],
          ...updatedConversation,
          kanban_cards:
            updatedConversation.kanban_cards ||
            column.conversations[index].kanban_cards,
        };
      }
    }
  });

  // Force reactivity update by creating a new array reference
  columns.value = [...columns.value];
};

// Auto-scroll functions
const startAutoScroll = direction => {
  if (scrollInterval.value) return;

  const speed = direction === 'left' ? -8 : 8;
  scrollSpeed.value = speed;

  scrollInterval.value = setInterval(() => {
    if (scrollContainer.value && isDragging.value) {
      scrollContainer.value.scrollLeft += speed;
    }
  }, 16); // ~60fps
};

const stopAutoScroll = () => {
  if (scrollInterval.value) {
    clearInterval(scrollInterval.value);
    scrollInterval.value = null;
    scrollSpeed.value = 0;
  }
};

const handleDragStart = () => {
  isDragging.value = true;
};

const handleDragEnd = () => {
  isDragging.value = false;
  stopAutoScroll();
};

const handleMouseMove = event => {
  // Debug: verificar se está arrastando
  if (!isDragging.value) return;
  if (!scrollContainer.value) return;

  const containerRect = scrollContainer.value.getBoundingClientRect();
  const scrollThreshold = 100; // Área de detecção aumentada

  const mouseX = event.clientX;
  const leftEdge = containerRect.left + scrollThreshold;
  const rightEdge = containerRect.right - scrollThreshold;

  if (mouseX < leftEdge) {
    startAutoScroll('left');
  } else if (mouseX > rightEdge) {
    startAutoScroll('right');
  } else {
    stopAutoScroll();
  }
};

watch(
  () => pipelineId.value,
  async id => {
    if (id) {
      // Resetar filtros para padrão quando trocar de funil
      appliedFilters.value = [
        {
          attributeKey: 'status',
          filterOperator: 'equal_to',
          values: ['all'],
          queryOperator: 'and',
        },
      ];
      await loadPipelineBoard(id);
    }
  },
  { immediate: true }
);

// Listener para eventos de mudança de funil
const handleFunnelUpdate = event => {
  const { conversationId, action, fromColumnId, toColumnId } = event.detail;

  if (action === 'create') {
    // Se um novo funil foi criado, recarregar o board do Kanban
    setTimeout(async () => {
      try {
        await loadPipelineBoard(pipelineId.value);
      } catch (error) {
        console.error('Erro ao recarregar board do Kanban:', error);
      }
    }, 500);
  } else if (action === 'move' && fromColumnId && toColumnId) {
    // Se uma conversa foi movida no chat, sincronizar no Kanban
    setTimeout(async () => {
      try {
        // Encontrar a conversa e movê-la visualmente
        const fromColumn = columns.value.find(col => col.id === fromColumnId);
        const toColumn = columns.value.find(col => col.id === toColumnId);

        if (fromColumn && toColumn) {
          const conversation = fromColumn.conversations?.find(
            conv => conv.id === conversationId
          );

          if (conversation) {
            // Remover da coluna origem
            fromColumn.conversations = fromColumn.conversations.filter(
              conv => conv.id !== conversationId
            );

            // Adicionar à coluna destino
            if (!toColumn.conversations) toColumn.conversations = [];
            toColumn.conversations.push(conversation);

            // Forçar reatividade
            columns.value = [...columns.value];
          } else {
            await loadPipelineBoard(pipelineId.value);
          }
        } else {
          await loadPipelineBoard(pipelineId.value);
        }
      } catch (error) {
        console.error('Erro ao sincronizar movimento no Kanban:', error);
      }
    }, 200);
  }
};

// Listener para eventos de mudança de produtos
const handleProductsUpdate = event => {
  const { action, conversationId, source, cardId } = event.detail;

  // Se a mudança veio do próprio kanban, atualizar apenas o card específico
  if (source === 'kanban' || (action && action.startsWith('kanban_product_'))) {
    updateSpecificCard(conversationId, cardId);
    return;
  }

  // Se a ação vem de outras páginas (conversas/contatos), recarregar
  if (
    pipelineId.value &&
    (action === 'product_added' ||
      action === 'product_updated' ||
      action === 'product_removed')
  ) {
    loadPipelineBoard(pipelineId.value);
  }
};

// Função para carregar mais conversas de uma coluna específica
const handleLoadMore = async columnId => {
  try {
    const column = columns.value.find(col => col.id === columnId);
    if (!column || !column.pagination) return;

    const nextPage = column.pagination.current_page + 1;

    // Carregar próxima página
    const data = await loadMoreConversations(pipelineId.value, nextPage);

    // Encontrar a coluna atualizada nos dados retornados
    const updatedColumn = data.find(col => col.id === columnId);
    if (!updatedColumn) return;

    // Adicionar novas conversas à coluna existente (append)
    column.conversations = [
      ...(column.conversations || []),
      ...(updatedColumn.conversations || []),
    ];
    column.pagination = updatedColumn.pagination;

    // Forçar reatividade
    columns.value = [...columns.value];
  } catch (error) {
    console.error('❌ Error loading more conversations:', error);
    useAlert(t('KANBAN.LOAD_MORE_ERROR'));
  }
};

// Listener para eventos de mudança de status do card
const handleCardStatusUpdate = event => {
  const {
    conversationId,
    cardId,
    status,
    pipelineId: eventPipelineId,
  } = event.detail;

  // Verificar se é do pipeline atual
  if (eventPipelineId && String(eventPipelineId) !== String(pipelineId.value)) {
    return;
  }

  // Atualizar o status do card localmente
  columns.value.forEach(column => {
    if (column.conversations) {
      column.conversations.forEach(conversation => {
        if (conversation.id === conversationId) {
          if (conversation.kanban_cards) {
            const cardIndex = conversation.kanban_cards.findIndex(
              card => card.id === cardId
            );
            if (cardIndex !== -1) {
              conversation.kanban_cards[cardIndex].status = status;
            }
          }
        }
      });
    }
  });
};

// Função para atualizar apenas um card específico
const updateSpecificCard = async (conversationId, cardId) => {
  if (!pipelineId.value || !cardId) return;

  try {
    // Buscar dados atualizados do card específico
    const response = await kanbanCardsAPI.getCard(pipelineId.value, cardId);
    const updatedCard = response.data;

    // Encontrar e atualizar o card nas colunas
    let cardFound = false;

    columns.value.forEach(column => {
      if (column.conversations) {
        column.conversations.forEach(conversation => {
          // Se conversationId foi fornecido, usar como filtro
          if (conversationId && conversation.id !== conversationId) {
            return;
          }

          // Atualizar os dados do kanban_card específico
          if (conversation.kanban_cards) {
            const cardIndex = conversation.kanban_cards.findIndex(
              card => card.id === cardId
            );
            if (cardIndex !== -1) {
              // Atualizar o card específico
              conversation.kanban_cards[cardIndex] = {
                ...conversation.kanban_cards[cardIndex],
                ...updatedCard,
              };
              cardFound = true;
            }
          }
        });
      }
    });

    if (!cardFound) {
      console.warn('⚠️ Card não encontrado, recarregando board...');
      loadPipelineBoard(pipelineId.value);
    } else {
      // Forçar reatividade
      columns.value = [...columns.value];
    }
  } catch (error) {
    console.error('❌ Erro ao atualizar card específico:', error);
    // Fallback: recarregar tudo se houver erro
    loadPipelineBoard(pipelineId.value);
  }
};

// Estado para controlar se os dados de filtros foram carregados
const filterDataLoaded = ref(false);
const filterModalKey = ref(0);
const filterDataReady = ref(false);

// Função para carregar dados necessários para os filtros
const loadFilterData = async () => {
  try {
    // Carregar dados essenciais apenas
    await Promise.all([
      store.dispatch('agents/get'),
      store.dispatch('inboxes/get'),
      store.dispatch('teams/get'),
      store.dispatch('campaigns/get'),
      store.dispatch('labels/get'),
    ]);

    // Marcar como carregado
    filterDataLoaded.value = true;
    filterDataReady.value = true;
    filterModalKey.value++;
  } catch (error) {
    // Mesmo com erro, marcar como carregado para não travar a interface
    filterDataLoaded.value = true;
    filterDataReady.value = true;
    filterModalKey.value++;
  }
};

// Listener para WebSocket de movimentação do kanban
const handleKanbanConversationMoved = event => {
  const {
    conversationId,
    fromColumnId,
    toColumnId,
    pipelineId: eventPipelineId,
  } = event.detail;

  // Verificar se é do pipeline atual
  if (String(eventPipelineId) !== String(pipelineId.value)) {
    return;
  }

  // Encontrar as colunas
  const fromColumn = columns.value.find(col => col.id === fromColumnId);
  const toColumn = columns.value.find(col => col.id === toColumnId);

  if (!fromColumn || !toColumn) {
    return;
  }

  // Debug: ver estrutura das conversas

  // Encontrar a conversa na coluna de origem (pode ser id ou display_id)
  const conversation = fromColumn.conversations?.find(
    conv => conv.id === conversationId || conv.display_id === conversationId
  );

  if (conversation) {
    // Remover da coluna de origem (verificar ambos os IDs)
    fromColumn.conversations = fromColumn.conversations.filter(
      conv => conv.id !== conversationId && conv.display_id !== conversationId
    );

    // Adicionar à coluna de destino (verificar se já não está lá)
    if (!toColumn.conversations) toColumn.conversations = [];
    const alreadyExists = toColumn.conversations.some(
      conv => conv.id === conversationId || conv.display_id === conversationId
    );
    if (!alreadyExists) {
      toColumn.conversations.push(conversation);
    }

    // Forçar reatividade
    columns.value = [...columns.value];
  } else {
    // Se não encontrou, recarregar o board inteiro
    if (pipelineId.value) {
      loadPipelineBoard(pipelineId.value);
    }
  }
};

// Listeners para WebSocket de cards
const handleKanbanCardCreated = event => {
  const { kanbanColumnId, pipelineId: eventPipelineId } = event.detail;

  if (String(eventPipelineId) !== String(pipelineId.value)) {
    return;
  }

  // Recarregar apenas a coluna específica ou o board inteiro
  loadPipelineBoard(pipelineId.value);
};

const handleKanbanCardDeleted = event => {
  const {
    kanbanCardId,
    kanbanColumnId,
    pipelineId: eventPipelineId,
  } = event.detail;

  if (String(eventPipelineId) !== String(pipelineId.value)) {
    return;
  }

  // Encontrar e remover o card da coluna
  const column = columns.value.find(col => col.id === kanbanColumnId);
  if (column && column.conversations) {
    column.conversations = column.conversations.filter(conv => {
      const card = conv.kanban_cards?.find(c => c.id === kanbanCardId);
      return !card;
    });
    columns.value = [...columns.value];
  }
};

// Listeners para WebSocket de produtos
const handleKanbanProductAdded = event => {
  const { kanbanCardId, pipelineId: eventPipelineId } = event.detail;

  if (String(eventPipelineId) !== String(pipelineId.value)) {
    return;
  }

  // Atualizar apenas o card específico
  updateSpecificCard(null, kanbanCardId);
};

const handleKanbanProductUpdated = event => {
  const { kanbanCardId, pipelineId: eventPipelineId } = event.detail;

  if (String(eventPipelineId) !== String(pipelineId.value)) {
    return;
  }

  updateSpecificCard(null, kanbanCardId);
};

const handleKanbanProductRemoved = event => {
  const { kanbanCardId, pipelineId: eventPipelineId } = event.detail;

  if (String(eventPipelineId) !== String(pipelineId.value)) {
    return;
  }

  updateSpecificCard(null, kanbanCardId);
};

// Handlers para mudanças de assignee, priority e labels
const handleKanbanAssigneeChanged = event => {
  const { conversationId, assigneeId, assignee } = event.detail;

  // Encontrar a conversa em todas as colunas e atualizar o assignee
  columns.value.forEach(column => {
    if (column.conversations) {
      const conversation = column.conversations.find(
        conv => conv.id === conversationId
      );
      if (conversation) {
        conversation.assignee = assignee;
        conversation.assignee_id = assigneeId;
        // Forçar reatividade
        columns.value = [...columns.value];
      }
    }
  });
};

const handleKanbanPriorityChanged = event => {
  const { conversationId, priority } = event.detail;

  // Encontrar a conversa em todas as colunas e atualizar a prioridade
  columns.value.forEach(column => {
    if (column.conversations) {
      const conversation = column.conversations.find(
        conv => conv.id === conversationId
      );
      if (conversation) {
        conversation.priority = priority;
        // Forçar reatividade
        columns.value = [...columns.value];
      }
    }
  });
};

const handleKanbanLabelsChanged = event => {
  const { conversationId, labels } = event.detail;

  // Encontrar a conversa em todas as colunas e atualizar as labels
  columns.value.forEach(column => {
    if (column.conversations) {
      const conversation = column.conversations.find(
        conv => conv.id === conversationId
      );
      if (conversation) {
        conversation.labels = labels;
        conversation.label_list = labels;
        // Forçar reatividade
        columns.value = [...columns.value];
      }
    }
  });
};

onMounted(async () => {
  if (pipelineId.value) {
    loadPipelineBoard(pipelineId.value);
  }

  // Carregar dados necessários para os filtros
  await loadFilterData();

  // Carregar lista de pipelines
  const { fetchPipelines } = useKanban();
  try {
    const pipelineData = await fetchPipelines();
    pipelines.value = Array.isArray(pipelineData) ? pipelineData : [];
  } catch (error) {
    console.error('Erro ao carregar pipelines:', error);
  }

  // Add mouse move listener for auto-scroll
  document.addEventListener('mousemove', handleMouseMove);

  // Listener para eventos de mudança de funil
  window.addEventListener('funnelUpdated', handleFunnelUpdate);

  // Listener para eventos de mudança de produtos
  window.addEventListener('productsUpdated', handleProductsUpdate);

  // Listener para eventos de mudança de status do card
  window.addEventListener('kanbanCardStatusUpdated', handleCardStatusUpdate);

  // Listener para WebSocket de movimentação do kanban
  window.addEventListener(
    'kanbanConversationMoved',
    handleKanbanConversationMoved
  );

  // Listeners para WebSocket de cards
  window.addEventListener('kanbanCardCreated', handleKanbanCardCreated);
  window.addEventListener('kanbanCardDeleted', handleKanbanCardDeleted);

  // Listeners para WebSocket de produtos
  window.addEventListener('kanbanProductAdded', handleKanbanProductAdded);
  window.addEventListener('kanbanProductUpdated', handleKanbanProductUpdated);
  window.addEventListener('kanbanProductRemoved', handleKanbanProductRemoved);

  // Listeners para WebSocket de assignee, priority e labels
  window.addEventListener('kanbanAssigneeChanged', handleKanbanAssigneeChanged);
  window.addEventListener('kanbanPriorityChanged', handleKanbanPriorityChanged);
  window.addEventListener('kanbanLabelsChanged', handleKanbanLabelsChanged);
});

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('funnelUpdated', handleFunnelUpdate);
  window.removeEventListener('productsUpdated', handleProductsUpdate);
  window.removeEventListener('kanbanCardStatusUpdated', handleCardStatusUpdate);
  window.removeEventListener(
    'kanbanConversationMoved',
    handleKanbanConversationMoved
  );
  window.removeEventListener('kanbanCardCreated', handleKanbanCardCreated);
  window.removeEventListener('kanbanCardDeleted', handleKanbanCardDeleted);
  window.removeEventListener('kanbanProductAdded', handleKanbanProductAdded);
  window.removeEventListener(
    'kanbanProductUpdated',
    handleKanbanProductUpdated
  );
  window.removeEventListener(
    'kanbanProductRemoved',
    handleKanbanProductRemoved
  );
  window.removeEventListener(
    'kanbanAssigneeChanged',
    handleKanbanAssigneeChanged
  );
  window.removeEventListener(
    'kanbanPriorityChanged',
    handleKanbanPriorityChanged
  );
  window.removeEventListener('kanbanLabelsChanged', handleKanbanLabelsChanged);
  stopAutoScroll();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Kanban Header with filters -->
    <KanbanHeader
      :pipeline-name="pipeline?.name || ''"
      :pipeline-description="pipeline?.description || ''"
      :total-conversations="totalConversations"
      :column-stats="columnStats"
      :current-card-status-filter="currentCardStatusFilter"
      :has-applied-filters="hasAppliedFilters"
      :pipelines="pipelines"
      :current-pipeline="pipeline"
      :show-pipeline-dropdown="showPipelineDropdown"
      @add-column="handleAddColumn"
      @update-card-status-filter="handleCardStatusFilterChange"
      @open-conversation-filters="openConversationFilterModal"
      @clear-all-filters="clearAllFilters"
      @select-pipeline="handleSelectPipeline"
      @toggle-pipeline-dropdown="handleTogglePipelineDropdown"
      @create-pipeline="handleCreatePipeline"
      @manage-pipelines="handleManagePipelines"
    />

    <!-- Kanban Board Content -->
    <div class="flex-1 overflow-hidden">
      <div class="h-full p-6">
        <div
          v-if="loading && columns.length === 0"
          class="flex items-center justify-center h-64"
        >
          <div class="text-center">
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-slate-9 mx-auto"
            />
            <p class="text-n-slate-11 mt-2">{{ t('KANBAN.LOADING') }}</p>
          </div>
        </div>

        <div
          v-else-if="error"
          class="flex items-center justify-center h-64 text-red-500"
        >
          {{ t('GENERAL.ERROR_DEFAULT') }}
        </div>

        <div
          v-else-if="!columns.length"
          class="flex items-center justify-center h-64 text-n-slate-11"
        >
          <div class="text-center">
            <p>{{ t('KANBAN.PIPELINES.EMPTY_COLUMNS') }}</p>
            <Button
              class="mt-4"
              :label="t('KANBAN.PIPELINES.ADD_FIRST_COLUMN')"
              @click="
                router.push({
                  name: 'kanban_pipeline_edit',
                  params: { pipelineId: pipeline?.id },
                })
              "
            />
          </div>
        </div>

        <div
          ref="scrollContainer"
          class="flex gap-6 h-full overflow-x-auto pb-4"
          @dragstart="handleDragStart"
          @dragend="handleDragEnd"
        >
          <KanbanColumn
            v-for="column in filteredColumns"
            :key="column.id"
            :column="column"
            :all-columns="columns"
            :pipeline-id="pipelineId"
            @move-conversation="moveConversationHandler"
            @delete-conversation="handleDeleteConversation"
            @update-conversation="handleUpdateConversation"
            @drag-start="handleDragStart"
            @drag-end="handleDragEnd"
            @load-more="handleLoadMore"
          />
        </div>
      </div>
    </div>

    <!-- Add Column Modal -->
    <AddColumnModal
      :show="showAddColumnModal"
      :loading="addColumnLoading"
      @close="handleCloseAddColumnModal"
      @save="handleSaveColumn"
    />

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="cancelDeleteConversation"
    >
      <div class="bg-n-background rounded-lg shadow-lg w-full max-w-md m-4">
        <div class="p-6">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"
            >
              <i class="i-lucide-alert-triangle size-5 text-red-600" />
            </div>
            <div>
              <h3 class="font-semibold text-n-slate-12">Remover do Funil</h3>
              <p class="text-sm text-n-slate-11">
                Esta ação não pode ser desfeita
              </p>
            </div>
          </div>

          <p class="text-n-slate-11 mb-6">
            Tem certeza que deseja remover esta conversa do funil? A conversa
            continuará existindo, mas será removida deste pipeline de vendas.
          </p>

          <div class="flex gap-3 justify-end">
            <button
              class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="cancelDeleteConversation"
            >
              Cancelar
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              @click="confirmDeleteConversation"
            >
              Sim, Remover
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Conversation Filter Modal -->
    <div
      v-if="showConversationFilterModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <ConversationFilter
        v-model="appliedFilters"
        :hide-clear-button="true"
        :hide-trash-icon="true"
        @apply-filter="handleApplyFilters"
        @close="closeConversationFilterModal"
      />
    </div>
  </div>
</template>

<style scoped>
/* Scroll horizontal customizado */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: rgba(156, 163, 175, 0.1);
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
}
</style>
